import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { customerResolutionModeSchema } from "./customer-resolution-mode";

/**
 * Create Sales Order Draft From Document
 *
 * Single-document path aligned with `POST .../sales-order-drafts/upload`:
 * optional customer match from file (unless a customer is wired in), then
 * Extend OCR + staged PO creation (`processSinglePODocumentFromUpload`).
 * Intended for split_document_v2 child documents and email-centric flows.
 *
 * Wire optional `inboundEmail` + `customer` from **Identify Customer** so
 * provenance and the matched customer match that step without re-running
 * embeddings on the document (use a route / `others` socket — they emit an
 * AR_CUSTOMER entity ref).
 */
export const createSalesOrderDraftFromDocumentStep = defineStepConfig({
  type: "create_sales_order_draft_from_document",
  name: "Create Sales Order Draft From Document",
  description:
    "Create sales order draft(s) from one document using the same pipeline as the draft upload API",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Legacy staged-PO flow. Use 'Create Draft Sales Order', which persists a relational ar_draft_sales_orders header + line items (stable line ids).",
    replacedBy: "create_draft_sales_order",
  },
  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document to extract a purchase order from and create draft(s) for",
    ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Inbound email for draft provenance (sourceId). When set, must be linked to this document (or an ancestor). Typically the same email passed into Identify Customer.",
      ),
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .optional()
      .describe(
        "Pre-matched customer — wire from an Identify Customer route or `others` socket (AR_CUSTOMER entity ref). Skips document-based customer matching when present.",
      ),
  }),

  outputSchema: z.object({
    stagedOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of staged purchase orders created from this document"),
    firstStagedOrderId: z
      .string()
      .uuid()
      .optional()
      .describe("First staged PO id when at least one draft was created"),
    sourceDocumentId: z
      .string()
      .uuid()
      .describe("The document id that was processed"),
  }),

  configSchema: z
    .object({
      customerId: z
        .uuid()
        .optional()
        .describe(
          "Pre-identified customer ID — skips file-based customer matching when set with customerResolutionMode known",
        ),
      customerResolutionMode: customerResolutionModeSchema
        .optional()
        .describe(
          '"known" uses config customerId only. Other modes use upload-style matching (embeddings) inside the document pipeline.',
        ),
    })
    .refine(
      (config) =>
        config.customerResolutionMode !== "known" || !!config.customerId,
      {
        message: 'customerResolutionMode "known" requires customerId to be set',
        path: ["customerId"],
      },
    ),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
